"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ChevronDown, ChevronRight, MessageSquare, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import type { TaskSummaryData } from "@/lib/types/ai";

export interface TaskAISummaryContentProps {
  displayedContent: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  summary: TaskSummaryData | null;
  showMetadata: boolean;
  className?: string;
}

/**
 * Content display component for task AI summary
 * Shows markdown content with collapsible teaser and metadata
 */
export function TaskAISummaryContent({
  displayedContent,
  isExpanded,
  onToggleExpanded,
  summary,
  showMetadata,
  className,
}: TaskAISummaryContentProps) {
  const markdownComponent = useMemo(() => {
    if (!displayedContent) return null;

    return (
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight]}
          components={{
            h1: ({ children }) => <h1 className="text-lg font-semibold mb-2">{children}</h1>,
            h2: ({ children }) => <h2 className="text-base font-semibold mb-2">{children}</h2>,
            h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
            p: ({ children }) => <p className="mb-2 text-sm leading-relaxed">{children}</p>,
            ul: ({ children }) => (
              <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>
            ),
            li: ({ children }) => <li className="text-sm">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            code: ({ children }) => (
              <code className="bg-card-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 dark:border-gray-700 dark:text-gray-400">
                {children}
              </blockquote>
            ),
          }}
        >
          {displayedContent}
        </ReactMarkdown>
      </div>
    );
  }, [displayedContent]);

  return (
    <div className={cn("space-y-3", className)}>
      {/* Teaser Bar */}
      <div
        className="flex items-center justify-between p-3 bg-card-50 dark:bg-card-800 rounded-lg cursor-pointer hover:bg-card-100 dark:hover:bg-card-700 transition-colors"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-sm font-medium">AI Summary Ready</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Click to expand</span>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Full Content */}
      {isExpanded && (
        <div className="space-y-3">
          {markdownComponent}

          {showMetadata && summary?.metadata && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Badge variant="secondary" className="text-xs">
                <MessageSquare className="h-3 w-3 mr-1" />
                {summary.metadata.noteCount} notes analyzed
              </Badge>
              <Badge variant="secondary" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {summary.metadata.processingTime}ms
              </Badge>
              <Badge variant="secondary" className="text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                {new Date(summary.createdAt).toLocaleString()}
              </Badge>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
